<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { RouterLink } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores } = proxy;

const productsStore = $stores?.products;
const featuredProducts = ref([]);
const popularProducts = ref([]);
const loading = ref(true);

onMounted(async () => {
  try {
    if (!productsStore?.state?.allProducts?.length) {
      await productsStore?.fetchProducts();
    }
    featuredProducts.value = productsStore?.state?.featuredProducts?.slice(0, 8) || [];
    const allProducts = productsStore?.state?.allProducts || [];
    const featuredIds = new Set(featuredProducts.value.map(p => p.id));
    popularProducts.value = allProducts.filter(p => !featuredIds.has(p.id)).slice(0, 8);
  } catch (error) {
    console.error('Erreur chargement produits:', error);
  } finally {
    loading.value = false;
  }
});

const streamingProducts = computed(() =>
  featuredProducts.value.filter(p => p.category === 'subscription')
);

// Tous les produits depuis le store
const products = computed(() => productsStore?.state?.allProducts || []);

// Helper to find product by name
const findProductByName = (name) => {
  return products.value.find(p =>
    p.name?.toLowerCase().includes(name.toLowerCase()) ||
    p.title?.toLowerCase().includes(name.toLowerCase())
  );
};
</script>

<template>
  <div class="min-h-screen" style="font-family: 'Plus Jakarta Sans', sans-serif; background-color: #0A0A0B;">
    <!-- Hero Section -->
    <section class="min-h-screen py-20 relative overflow-hidden">
      <div class="absolute inset-0" style="background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(55, 182, 233, 0.3), transparent), radial-gradient(ellipse 60% 40% at 80% 50%, rgba(75, 76, 237, 0.15), transparent), radial-gradient(ellipse 50% 30% at 20% 80%, rgba(255, 56, 92, 0.15), transparent);"></div>

      <div class="relative z-10 px-4 max-w-7xl mx-auto">
        <!-- Hero Text -->
        <div class="text-center mb-16">
          <h1 class="font-black text-[clamp(2.5rem,8vw,6rem)] leading-none mb-6" style="font-family: 'Outfit', sans-serif;">
            <span class="text-white">Payez vos</span><br/>
            <span style="background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">abonnements</span><br/>
            <span class="text-white">moins cher</span>
          </h1>

          <p class="text-lg md:text-xl max-w-2xl mx-auto mb-10" style="color: rgba(255, 255, 255, 0.7);">
            Découvrez une large gamme d'abonnements numériques à des prix imbattables.
            Netflix, Spotify, Disney+, VPN et bien plus encore.
          </p>

          <div class="flex flex-wrap gap-4 justify-center">
            <RouterLink to="/boutique" class="px-8 py-4 rounded-2xl font-semibold text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 inline-flex" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 50%, #257e9f 100%);">
              Voir la boutique
            </RouterLink>
            <RouterLink to="#featured" class="px-8 py-4 rounded-2xl font-semibold text-white border border-white/10 hover:bg-white/5 transition-all duration-300 inline-flex">
              En savoir plus
            </RouterLink>
          </div>
        </div>

        <!-- Large Product Cards - Hero Grid -->
        <div v-if="loading" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <!-- Loading Skeletons -->
          <div v-for="i in 6" :key="i" class="rounded-3xl overflow-hidden animate-pulse" style="background: rgba(26, 26, 29, 0.8); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6">
              <div class="w-24 h-24 rounded-full" style="background: rgba(255, 255, 255, 0.05);"></div>
            </div>
            <div class="p-5 text-center space-y-2">
              <div class="h-5 rounded mx-auto" style="background: rgba(255, 255, 255, 0.05); width: 80%;"></div>
              <div class="h-3 rounded mx-auto" style="background: rgba(255, 255, 255, 0.03); width: 60%;"></div>
              <div class="h-6 rounded mx-auto mt-3" style="background: rgba(55, 182, 233, 0.1); width: 70%;"></div>
            </div>
          </div>
        </div>

        <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <!-- Netflix Card -->
          <RouterLink v-if="findProductByName('netflix')" :to="`/produit/${findProductByName('netflix').id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(229, 9, 20, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(229, 9, 20, 0.2) 0%, transparent 70%);"></div>
              <img v-if="findProductByName('netflix').image" :src="findProductByName('netflix').image" alt="Netflix" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <img v-else src="/kingrechargeAssets/netflix.svg" alt="Netflix" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Netflix</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">{{ findProductByName('netflix')?.priceDisplay || '2 500' }} CFA</p>
            </div>
          </RouterLink>
          <RouterLink v-else to="/boutique" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(229, 9, 20, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(229, 9, 20, 0.2) 0%, transparent 70%);"></div>
              <img src="/kingrechargeAssets/netflix.svg" alt="Netflix" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Netflix</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">2 500 CFA</p>
            </div>
          </RouterLink>

          <!-- Spotify Card -->
          <RouterLink v-if="findProductByName('spotify')" :to="`/produit/${findProductByName('spotify').id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(30, 215, 96, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(30, 215, 96, 0.2) 0%, transparent 70%);"></div>
              <img v-if="findProductByName('spotify').image" :src="findProductByName('spotify').image" alt="Spotify" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <img v-else src="/kingrechargeAssets/spotify.svg" alt="Spotify" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Spotify</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">{{ findProductByName('spotify')?.priceDisplay || '2 000' }} CFA</p>
            </div>
          </RouterLink>
          <RouterLink v-else to="/boutique" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(30, 215, 96, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(30, 215, 96, 0.2) 0%, transparent 70%);"></div>
              <img src="/kingrechargeAssets/spotify.svg" alt="Spotify" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Spotify</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">2 000 CFA</p>
            </div>
          </RouterLink>

          <!-- Disney+ Card -->
          <RouterLink v-if="findProductByName('disney')" :to="`/produit/${findProductByName('disney').id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(113, 186, 229, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(113, 186, 229, 0.2) 0%, transparent 70%);"></div>
              <img v-if="findProductByName('disney').image" :src="findProductByName('disney').image" alt="Disney+" class="w-24 h-24 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <span v-else class="text-7xl relative z-10 group-hover:scale-110 transition-transform duration-300">🏰</span>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Disney+</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">{{ findProductByName('disney')?.priceDisplay || '2 500' }} CFA</p>
            </div>
          </RouterLink>
          <RouterLink v-else to="/boutique" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(113, 186, 229, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(113, 186, 229, 0.2) 0%, transparent 70%);"></div>
              <span class="text-7xl relative z-10 group-hover:scale-110 transition-transform duration-300">🏰</span>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Disney+</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">2 500 CFA</p>
            </div>
          </RouterLink>

          <!-- Apple TV Card -->
          <RouterLink v-if="findProductByName('apple')" :to="`/produit/${findProductByName('apple').id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);"></div>
              <img v-if="findProductByName('apple').image" :src="findProductByName('apple').image" alt="Apple TV" class="w-20 h-20 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <img v-else src="/kingrechargeAssets/apple-new-top-2-700x444.png" alt="Apple TV" class="w-20 h-20 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Apple TV</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">{{ findProductByName('apple')?.priceDisplay || '3 000' }} CFA</p>
            </div>
          </RouterLink>
          <RouterLink v-else to="/boutique" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);"></div>
              <img src="/kingrechargeAssets/apple-new-top-2-700x444.png" alt="Apple TV" class="w-20 h-20 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Apple TV</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">3 000 CFA</p>
            </div>
          </RouterLink>

          <!-- VPN Card -->
          <RouterLink v-if="findProductByName('cyberghost')" :to="`/produit/${findProductByName('cyberghost').id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(255, 165, 0, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(255, 165, 0, 0.2) 0%, transparent 70%);"></div>
              <img v-if="findProductByName('cyberghost').image" :src="findProductByName('cyberghost').image" alt="VPN" class="w-20 h-20 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <img v-else src="/kingrechargeAssets/cyberghost.webp" alt="VPN" class="w-20 h-20 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">CyberGhost</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">{{ findProductByName('cyberghost')?.priceDisplay || '3 500' }} CFA</p>
            </div>
          </RouterLink>
          <RouterLink v-else to="/boutique" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(180deg, rgba(26, 26, 29, 0.5) 0%, rgba(255, 165, 0, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(255, 165, 0, 0.2) 0%, transparent 70%);"></div>
              <img src="/kingrechargeAssets/cyberghost.webp" alt="VPN" class="w-20 h-20 relative z-10 group-hover:scale-110 transition-transform duration-300"/>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">CyberGhost</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">à partir de</p>
              <p class="font-bold text-xl mt-1" style="color: #37b6e9;">3 500 CFA</p>
            </div>
          </RouterLink>

          <!-- More Card -->
          <RouterLink to="/boutique" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(55, 182, 233, 0.2);">
            <div class="aspect-[4/5] flex items-center justify-center p-6 relative" style="background: linear-gradient(135deg, rgba(55, 182, 233, 0.15) 0%, rgba(75, 76, 237, 0.1) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(55, 182, 233, 0.3) 0%, transparent 70%);"></div>
              <div class="text-center relative z-10">
                <span class="text-6xl block mb-2 group-hover:scale-110 transition-transform duration-300">🎬</span>
                <span class="material-icons-outlined text-4xl text-white/60 group-hover:text-white transition-colors">arrow_forward</span>
              </div>
            </div>
            <div class="p-5 text-center">
              <h3 class="font-bold text-lg mb-2 text-white" style="font-family: 'Outfit', sans-serif;">Voir tout</h3>
              <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">+50 produits</p>
              <p class="font-bold text-xl mt-1" style="color: #4b4ced;">Boutique →</p>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="py-20" style="background-color: #111113;">
      <div class="max-w-7xl mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="rounded-3xl p-8 text-center" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="text-5xl mb-2" style="font-family: 'Outfit', sans-serif; font-weight: 800; background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">50K+</div>
            <div style="color: rgba(255, 255, 255, 0.7);">Clients Satisfaits</div>
          </div>
          <div class="rounded-3xl p-8 text-center" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="text-5xl mb-2" style="font-family: 'Outfit', sans-serif; font-weight: 800; background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">24/7</div>
            <div style="color: rgba(255, 255, 255, 0.7);">Support Client</div>
          </div>
          <div class="rounded-3xl p-8 text-center" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="text-5xl mb-2" style="font-family: 'Outfit', sans-serif; font-weight: 800; background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">100%</div>
            <div style="color: rgba(255, 255, 255, 0.7);">Paiement Sécurisé</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Streaming Section -->
    <section id="featured" class="py-24">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="font-bold text-4xl md:text-6xl mb-4" style="font-family: 'Outfit', sans-serif;">
            <span style="background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Streaming Vidéo</span>
          </h2>
          <p class="text-lg max-w-2xl mx-auto" style="color: rgba(255, 255, 255, 0.7);">
            Accédez aux meilleures plateformes de streaming à des prix exceptionnels
          </p>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <RouterLink
            v-for="(product, index) in streamingProducts.slice(0, 5)"
            :key="product.id"
            :to="`/produit/${product.id}`"
            class="rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-8 hover:scale-105 group"
            style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);"
          >
            <div class="aspect-square flex items-center justify-center p-6" style="background-color: #1A1A1D;">
              <img v-if="product.image" :alt="product.name" :src="product.image" class="w-full h-full object-contain group-hover:scale-110 transition-transform"/>
              <span v-else class="text-5xl">🎬</span>
            </div>
            <div class="p-5">
              <h3 class="font-semibold text-lg mb-2" style="font-family: 'Outfit', sans-serif; color: white;">{{ product.name || product.title }}</h3>
              <p class="font-bold" style="color: #4b4ced;">{{ product.priceDisplay || `${product.price} CFA` }}</p>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="py-24" style="background-color: #111113;">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="font-bold text-4xl md:text-6xl mb-4" style="font-family: 'Outfit', sans-serif;">
            <span style="background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Nos Offres</span>
          </h2>
          <p class="text-lg" style="color: rgba(255, 255, 255, 0.7);">
            Choisissez l'abonnement qui vous convient
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Basic Plan -->
          <RouterLink :to="`/produit/${featuredProducts[1]?.id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <!-- Product Image Header -->
            <div class="relative aspect-video flex items-center justify-center p-8" style="background: linear-gradient(180deg, rgba(16, 185, 129, 0.1) 0%, rgba(26, 26, 29, 0.8) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.15) 0%, transparent 70%);"></div>
              <img v-if="featuredProducts[1]?.image" :src="featuredProducts[1].image" :alt="featuredProducts[1]?.name" class="w-32 h-32 object-contain relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <span v-else class="text-7xl relative z-10">📱</span>
            </div>

            <div class="p-8 text-center">
              <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style="background: rgba(16, 185, 129, 0.15);">
                <span class="text-sm" style="color: #10B981;">●</span>
                <span class="text-sm font-semibold" style="color: #10B981;">Découverte</span>
              </div>

              <h3 class="text-2xl font-bold mb-2" style="font-family: 'Outfit', sans-serif; color: white;">{{ featuredProducts[1]?.name || 'Abonnement Essentiel' }}</h3>
              <p class="text-sm mb-6" style="color: rgba(255, 255, 255, 0.5);">Idéal pour découvrir le streaming</p>

              <div class="mb-8 pb-8" style="border-bottom: 1px solid rgba(255, 255, 255, 0.06);">
                <div class="font-black text-5xl mb-2" style="font-family: 'Outfit', sans-serif; color: white;">
                  {{ featuredProducts[1]?.priceDisplay || '2 000' }}
                </div>
                <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">par mois</p>
              </div>

              <div class="space-y-4 mb-8 text-left">
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(16, 185, 129, 0.15); color: #10B981; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">1 appareil</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Regardez sur téléphone ou tablette</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(16, 185, 129, 0.15); color: #10B981; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">HD 720p</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Qualité standard</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(16, 185, 129, 0.15); color: #10B981; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Sans engagement</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Résiliez à tout moment</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(16, 185, 129, 0.15); color: #10B981; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Support email</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Assistance 24h/24</p>
                  </div>
                </div>
              </div>

              <div class="w-full px-6 py-4 rounded-2xl font-semibold text-center border-2 border-white/10 group-hover:border-white/20 transition-all" style="color: white;">
                Choisir cette offre
              </div>
            </div>
          </RouterLink>

          <!-- Premium Plan - Featured -->
          <RouterLink :to="`/produit/${featuredProducts[0]?.id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105 relative" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 2px solid rgba(75, 76, 237, 0.4); box-shadow: 0 0 80px rgba(75, 76, 237, 0.2);">
            <!-- Popular Badge -->
            <div class="absolute top-0 left-0 right-0 py-3 text-center z-20" style="background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 50%, #4b4ced 100%);">
              <span class="text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2" style="color: #FFFFFF;">
                <span>★</span> Le plus choisi <span>★</span>
              </span>
            </div>

            <!-- Product Image Header -->
            <div class="relative aspect-video flex items-center justify-center p-8" style="background: linear-gradient(180deg, rgba(75, 76, 237, 0.15) 0%, rgba(26, 26, 29, 0.8) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(75, 76, 237, 0.2) 0%, transparent 70%);"></div>
              <img v-if="featuredProducts[0]?.image" :src="featuredProducts[0].image" :alt="featuredProducts[0]?.name" class="w-40 h-40 object-contain relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <span v-else class="text-8xl relative z-10">⭐</span>
            </div>

            <div class="p-8 text-center">
              <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style="background: rgba(75, 76, 237, 0.2);">
                <span class="text-sm" style="color: #4b4ced;">◆</span>
                <span class="text-sm font-semibold" style="color: #4b4ced;">Premium</span>
              </div>

              <h3 class="text-2xl font-bold mb-2" style="font-family: 'Outfit', sans-serif; color: #4b4ced;">{{ featuredProducts[0]?.name || 'Abonnement Premium' }}</h3>
              <p class="text-sm mb-6" style="color: rgba(255, 255, 255, 0.5);">Le choix de nos clients</p>

              <div class="mb-8 pb-8" style="border-bottom: 1px solid rgba(75, 76, 237, 0.2);">
                <div class="font-black text-6xl mb-2" style="font-family: 'Outfit', sans-serif; color: #4b4ced;">
                  {{ featuredProducts[0]?.priceDisplay || '2 500' }}
                </div>
                <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">par mois</p>
              </div>

              <div class="space-y-4 mb-8 text-left">
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(75, 76, 237, 0.2); color: #4b4ced; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">2 appareils</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">TV et mobile simultanément</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(75, 76, 237, 0.2); color: #4b4ced; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Full HD 1080p</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Haute définition</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(75, 76, 237, 0.2); color: #4b4ced; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Téléchargement offline</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Regardez sans internet</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(75, 76, 237, 0.2); color: #4b4ced; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Support prioritaire</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Réponse en 5min garantie</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(75, 76, 237, 0.2); color: #4b4ced; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Sans publicité</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Expérience 100% fluide</p>
                  </div>
                </div>
              </div>

              <div class="w-full px-6 py-4 rounded-2xl font-bold text-center transition-all hover:shadow-xl" style="background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 50%, #4b4ced 100%); color: #FFFFFF;">
                Choisir cette offre
              </div>
            </div>
          </RouterLink>

          <!-- Family Plan -->
          <RouterLink :to="`/produit/${featuredProducts[2]?.id}`" class="group rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:scale-105" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <!-- Product Image Header -->
            <div class="relative aspect-video flex items-center justify-center p-8" style="background: linear-gradient(180deg, rgba(55, 182, 233, 0.15) 0%, rgba(26, 26, 29, 0.8) 100%);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(55, 182, 233, 0.2) 0%, transparent 70%);"></div>
              <img v-if="featuredProducts[2]?.image" :src="featuredProducts[2].image" :alt="featuredProducts[2]?.name" class="w-32 h-32 object-contain relative z-10 group-hover:scale-110 transition-transform duration-300"/>
              <span v-else class="text-7xl relative z-10">👨‍👩‍👧‍👦</span>
            </div>

            <div class="p-8 text-center">
              <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style="background: rgba(55, 182, 233, 0.15);">
                <span class="text-sm" style="color: #37b6e9;">●</span>
                <span class="text-sm font-semibold" style="color: #37b6e9;">Famille</span>
              </div>

              <h3 class="text-2xl font-bold mb-2" style="font-family: 'Outfit', sans-serif; color: white;">{{ featuredProducts[2]?.name || 'Abonnement Famille' }}</h3>
              <p class="text-sm mb-6" style="color: rgba(255, 255, 255, 0.5);">Partagez avec vos proches</p>

              <div class="mb-8 pb-8" style="border-bottom: 1px solid rgba(255, 255, 255, 0.06);">
                <div class="font-black text-5xl mb-2" style="font-family: 'Outfit', sans-serif; color: white;">
                  {{ featuredProducts[2]?.priceDisplay || '3 800' }}
                </div>
                <p class="text-sm" style="color: rgba(255, 255, 255, 0.5);">par mois</p>
              </div>

              <div class="space-y-4 mb-8 text-left">
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(55, 182, 233, 0.15); color: #37b6e9; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">4 appareils</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Toute la famille connectée</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(55, 182, 233, 0.15); color: #37b6e9; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">4K Ultra HD</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Meilleure qualité disponible</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(55, 182, 233, 0.15); color: #37b6e9; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">5 profils</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Pour chaque membre</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(55, 182, 233, 0.15); color: #37b6e9; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Contrôle parental</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Protégez les enfants</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style="background: rgba(55, 182, 233, 0.15); color: #37b6e9; font-size: 0.7rem;">✓</span>
                  <div>
                    <span class="font-semibold text-white">Support VIP</span>
                    <p class="text-xs mt-1" style="color: rgba(255, 255, 255, 0.4);">Assistance dédiée 24/7</p>
                  </div>
                </div>
              </div>

              <div class="w-full px-6 py-4 rounded-2xl font-semibold text-center border-2 border-white/10 group-hover:border-white/20 transition-all" style="color: white;">
                Choisir cette offre
              </div>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- Why Us Section -->
    <section class="py-24">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="font-bold text-4xl md:text-6xl mb-4" style="font-family: 'Outfit', sans-serif;">
            <span style="background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Pourquoi Nous Choisir ?</span>
          </h2>
          <p class="text-lg" style="color: rgba(255, 255, 255, 0.7);">
            L'expérience KingRecharge Store en 3 points clés
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="rounded-3xl p-10" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="text-5xl mb-6">⚡</div>
            <h3 class="font-bold text-2xl mb-4 text-white" style="font-family: 'Outfit', sans-serif;">Livraison Instantanée</h3>
            <p class="leading-relaxed" style="color: rgba(255, 255, 255, 0.7);">
              Recevez vos codes d'activation par email ou WhatsApp en quelques minutes après votre paiement.
            </p>
          </div>
          <div class="rounded-3xl p-10" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="text-5xl mb-6">🔒</div>
            <h3 class="font-bold text-2xl mb-4 text-white" style="font-family: 'Outfit', sans-serif;">Paiement Sécurisé</h3>
            <p class="leading-relaxed" style="color: rgba(255, 255, 255, 0.7);">
              Vos transactions sont protégées par un cryptage SSL de pointe et nos partenaires de paiement fiables.
            </p>
          </div>
          <div class="rounded-3xl p-10" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="text-5xl mb-6">💬</div>
            <h3 class="font-bold text-2xl mb-4 text-white" style="font-family: 'Outfit', sans-serif;">Support 24/7</h3>
            <p class="leading-relaxed" style="color: rgba(255, 255, 255, 0.7);">
              Notre équipe est disponible à tout moment pour répondre à vos questions et vous accompagner.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="py-24" style="background-color: #111113;">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="font-bold text-4xl md:text-6xl mb-4" style="font-family: 'Outfit', sans-serif;">
            <span style="background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Avis Clients</span>
          </h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="rounded-3xl p-8" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="flex gap-1 mb-6 text-2xl" style="color: #4b4ced;">★★★★★</div>
            <p class="italic mb-8 leading-relaxed" style="color: rgba(255, 255, 255, 0.7);">
              "Service impeccable, reçu mon code en 2 minutes. Je recommande à 100% !"
            </p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);">JM</div>
              <div>
                <div class="font-semibold">Jean-Marc D.</div>
                <div class="text-sm" style="color: rgba(255, 255, 255, 0.3);">Client vérifié</div>
              </div>
            </div>
          </div>
          <div class="rounded-3xl p-8" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="flex gap-1 mb-6 text-2xl" style="color: #4b4ced;">★★★★★</div>
            <p class="italic mb-8 leading-relaxed" style="color: rgba(255, 255, 255, 0.7);">
              "Les meilleurs prix du marché et un client au top. Merci KingRecharge !"
            </p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold" style="background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 50%, #4b4ced 100%); color: #FFFFFF;">SA</div>
              <div>
                <div class="font-semibold">Sarah A.</div>
                <div class="text-sm" style="color: rgba(255, 255, 255, 0.3);">Cliente vérifiée</div>
              </div>
            </div>
          </div>
          <div class="rounded-3xl p-8" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="flex gap-1 mb-6 text-2xl" style="color: #4b4ced;">★★★★★</div>
            <p class="italic mb-8 leading-relaxed" style="color: rgba(255, 255, 255, 0.7);">
              "Abonnement Netflix fonctionnel et rapide. Très satisfait de mon achat."
            </p>
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);">PK</div>
              <div>
                <div class="font-semibold">Philippe K.</div>
                <div class="text-sm" style="color: rgba(255, 255, 255, 0.3);">Client vérifié</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-24">
      <div class="max-w-5xl mx-auto px-4">
        <div class="rounded-[40px] p-12 md:p-16 text-center relative overflow-hidden" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(55, 182, 233, 0.3);">
          <div class="absolute inset-0 opacity-50" style="background: radial-gradient(ellipse at top, rgba(55, 182, 233, 0.15), transparent 60%);"></div>
          <div class="relative z-10">
            <h2 class="font-bold text-4xl md:text-5xl mb-6" style="font-family: 'Outfit', sans-serif;">
              <span style="background: linear-gradient(135deg, #37b6e9 0%, #4adef2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Prêt à Commencer ?</span>
            </h2>
            <p class="text-lg max-w-2xl mx-auto mb-10" style="color: rgba(255, 255, 255, 0.7);">
              Rejoignez des milliers de clients satisfaits et profitez de nos offres exceptionnelles dès maintenant.
            </p>
            <RouterLink to="/boutique" class="px-12 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 inline-flex" style="background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 50%, #4b4ced 100%); color: #FFFFFF; font-size: 1.125rem;">
              Découvrir la Boutique
            </RouterLink>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}
</style>
